import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { SparePartsService } from 'src/app/services/spareParts/spare-parts.service';
import { environment } from 'src/environments/environment';
import { SparePartModel } from 'src/app/models/spare-part-model/spare-part-model';
import { CurrencyFormatter } from '../../utils/currency-formatter';
import { ErrorHandlerService } from '../../services/utils/error-handler.service';
import { getSaleValueValidationError } from '../../utils/spare-part-validation.util';
import { ALDANA_COLOR_OPTIONS } from 'src/app/constants/aldana-colors';
import { RETURN_CARRIER_OPTIONS, RETURN_LOG_STATUS_OPTIONS, RETURN_REASON_OPTIONS, RETURN_STATUS_OPTIONS, ML_STATUS_OPTIONS, FRETE_DEV_OPTIONS } from 'src/app/utils/return-options.util';
import { SPARE_PART_DAMAGED_OPTIONS, SPARE_PART_DAMAGED_NO } from 'src/app/constants/spare-part-damaged-states';
import { defaultFantUrgency, FantUrgency, normalizeFantUrgency } from 'src/app/utils/spare-part-payload.util';

@Component({
  selector: 'app-add-spare-part',
  templateUrl: './add-spare-part.component.html',
  styleUrls: ['./add-spare-part.component.css']
})
export class AddSparePartComponent {

  @Input() orderId?: number = 0;
  @Input() clienteUY?: string = 'AUTOCOMERCIAL';
  @Output() close = new EventEmitter<void>();
  sparePartDeliveryStatus = environment.sparePartDeliveryStatus;
  filteredSparePartDeliveryStatus: string[] = [];
  selectedSparePartArrivalDate: string = '';
  arrivalDays: number = 0; // Manejar como número directamente
  showAldanaForm = false;
  aldanaFechaComunicadoInput = '';
  aldanaColorOptions = ALDANA_COLOR_OPTIONS;
  readonly returnStatusOptions = RETURN_STATUS_OPTIONS;
  readonly returnLogStatusOptions = RETURN_LOG_STATUS_OPTIONS;
  readonly returnReasonOptions = RETURN_REASON_OPTIONS;
  readonly returnCarrierOptions = RETURN_CARRIER_OPTIONS;
  readonly mlStatusOptions = ML_STATUS_OPTIONS;
  readonly freteDevOptions = FRETE_DEV_OPTIONS;
  newSparePartMlDeadline = '';
  readonly damagedStateOptions = SPARE_PART_DAMAGED_OPTIONS;


  newSparePart: SparePartModel = {
    'sparePartId': 0,
    'sparePartName': '',
    'sparePartNameEs': '',
    'sparePartNamePt': '',
    'unitPrice': 0,
    'amount': 0,
    'productStatus': 'CREADO',
    'taxNote': '',
    'ncm': '',
    'productCode': '',
    'providerName': '',
    'returnStatus': '',
    'returnLogStatus': '',
    'returnReason': '',
    'returnDetails': '',
    'freteDev': '',
    'orderId': 0,
    'expectedArrivalDate': new Date(),
    'comments': '',
    'isDamaged': SPARE_PART_DAMAGED_NO,
    'codBr': '',
    'valorDeVenta': 0,
    'netWheight': 0,
    'grossWheight': 0,
    'country': '',
    'needReview': false,
    'isFant': false,
    'fantUrgency': null,
    'recompra': false,
    'shippingStatus': 'NO-ENVIADO',
    'shippingReceipt': '',
    'packageNumber': null,
    'exportNumber': null,
    'interno': '',
    'externo': '',
    'fechaDeComunicado': new Date(),
    'color': ''
  };

  newSparePartArrivalDate: Date = new Date();

  @ViewChild('daysUntilArrive') daysUntilArrival: ElementRef | undefined;

  constructor(
    private sparePartService: SparePartsService
  ) {
  }

  ngOnInit(): void {
    // Inicializar estados filtrados según el cliente al cargar el componente
    this.updateFilteredSparePartStatus(this.clienteUY || 'AUTOCOMERCIAL');
    this.aldanaFechaComunicadoInput = this.toInputDate(this.newSparePart.fechaDeComunicado ?? new Date());
  }

  private toInputDate(date: Date): string {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  }

  private parseInputDate(value: string): Date | undefined {
    if (!value) return undefined;
    const parsed = new Date(`${value}T00:00:00`);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  }

  toggleAldanaForm(): void {
    this.showAldanaForm = !this.showAldanaForm;
  }

  selectAldanaColor(color: string): void {
    this.newSparePart.color = color;
  }

  // Método para filtrar estados de repuestos según el cliente seleccionado
  updateFilteredSparePartStatus(clienteUY: string): void {
    if (clienteUY === 'AUTOCOMERCIAL') {
      this.filteredSparePartDeliveryStatus = this.sparePartDeliveryStatus.filter(status =>
        !status.includes('SERIMPORT'));
    } else if (clienteUY === 'SERIMPORT') {
      this.filteredSparePartDeliveryStatus = this.sparePartDeliveryStatus.filter(status =>
        !status.includes('AUTOCOMERCIAL'));
    } else {
      this.filteredSparePartDeliveryStatus = [...this.sparePartDeliveryStatus];
    }
  }
  // Función para normalizar valores numéricos usando la nueva utilidad
  normalizeNumericValue(value: number | string): number {
    return CurrencyFormatter.formatForBackend(value);
  }

  // Método para normalizar inputs de texto en la UI usando la nueva utilidad
  handleNumericInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    CurrencyFormatter.handleInput(inputElement, { maxDecimals: 2, allowNegative: false });
  }

  // Método para formatear al perder el foco
  handleNumericBlur(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    CurrencyFormatter.handleBlur(inputElement, { maxDecimals: 2, minDecimals: 2, allowNegative: false });
  }

  // Método para preparar el campo al recibir foco
  handleNumericFocus(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    CurrencyFormatter.handleFocus(inputElement);
  }

  closeSparePartModal(): void {
    this.close.emit(); // Cerrar el modal después de guardar
  }

  onFantCheckboxChanged(isFant: boolean): void {
    this.newSparePart.fantUrgency = defaultFantUrgency(isFant, null);
  }

  setFantUrgency(urgency: FantUrgency): void {
    if (!this.newSparePart.isFant) return;
    const current = normalizeFantUrgency(this.newSparePart.fantUrgency);
    this.newSparePart.fantUrgency = current === urgency ? null : urgency;
  }

  addSparePart(): void {
    if (this.validSparePart()) {
      //calcula la fecha de llegada esperada
      if (this.arrivalDays) {

        const expectedArrivalDate = new Date();
        expectedArrivalDate.setDate(expectedArrivalDate.getDate() + this.arrivalDays);

        this.newSparePart.expectedArrivalDate = expectedArrivalDate;
      }

      // Normalizar valores numéricos antes de enviar
      this.newSparePart.unitPrice = this.normalizeNumericValue(this.newSparePart.unitPrice);
      this.newSparePart.valorDeVenta = this.normalizeNumericValue(this.newSparePart.valorDeVenta);
      this.newSparePart.netWheight = this.normalizeNumericValue(this.newSparePart.netWheight);
      this.newSparePart.grossWheight = this.normalizeNumericValue(this.newSparePart.grossWheight);
      this.newSparePart.amount = this.normalizeNumericValue(this.newSparePart.amount);

      // Verificación de seguridad para garantizar que todos los valores numéricos son correctos
      if (isNaN(this.newSparePart.unitPrice) || this.newSparePart.unitPrice === null) {
        this.newSparePart.unitPrice = 0;
      }

      if (isNaN(this.newSparePart.valorDeVenta) || this.newSparePart.valorDeVenta === null) {
        this.newSparePart.valorDeVenta = 0;
      }

      if (isNaN(this.newSparePart.netWheight) || this.newSparePart.netWheight === null) {
        this.newSparePart.netWheight = 0;
      }

      if (isNaN(this.newSparePart.grossWheight) || this.newSparePart.grossWheight === null) {
        this.newSparePart.grossWheight = 0;
      }

      if (isNaN(this.newSparePart.amount) || this.newSparePart.amount === null) {
        this.newSparePart.amount = 0;
      }

      if (!this.orderId) {
        alert('No se ha seleccionado una orden');
        return;
      }
      this.newSparePart.fechaDeComunicado = this.parseInputDate(this.aldanaFechaComunicadoInput) ?? new Date();
      this.newSparePart.mlDeadline = this.parseInputDate(this.newSparePartMlDeadline);
      this.newSparePart.orderId = this.orderId;
      this.newSparePart.fantUrgency = defaultFantUrgency(!!this.newSparePart.isFant, this.newSparePart.fantUrgency);
      this.sparePartService.addSparePart(this.newSparePart, this.orderId).subscribe(() => {
        this.closeSparePartModal();
        //recargar la página
        location.reload();
      }, (error) => {
        ErrorHandlerService.logError(error, 'AddSparePartComponent');
        ErrorHandlerService.handleError(error, 'Error al agregar la pieza de repuesto');
      });
    }
  }

  validSparePart(): boolean {
    if (this.newSparePart.sparePartName === '') {
      alert('El nombre no puede estar vacío');
      return false;
    }
    if (this.newSparePart.amount === 0) {
      alert('La cantidad no puede ser 0');
      return false;
    }
    if (this.newSparePart.productCode === '') {
      alert('El código no puede estar vacío');
      return false;
    }
    if (this.newSparePart.productStatus === '') {
      alert('El estado no puede estar vacío');
      return false;
    }
    if (this.newSparePart.unitPrice === 0) {
      alert('El precio unitario no puede ser 0');
      return false;
    }
    const saleValueError = getSaleValueValidationError(
      this.newSparePart.valorDeVenta,
      this.newSparePart.sparePartName
    );
    if (saleValueError) {
      alert(saleValueError);
      return false;
    }
    if (this.newSparePart.netWheight === 0) {
      alert('El peso neto no puede ser 0');
      return false;
    }
    if (this.newSparePart.grossWheight === 0) {
      alert('El peso bruto no puede ser 0');
      return false;
    }
    return true;
  }
}
